import React, { Fragment, useRef, useState } from 'react';
import ContactModal from 'src/components/ContactModal';
import LineItemsCard from 'src/components/LineItemsCard';
import { LocationCard } from 'src/components/Locations';
import { ShipmentNotifications } from 'src/components/Notifications';
import { CarrierLogo } from 'src/components/RatePicker';
import ShareModal from 'src/components/ShareModal';
import ShipmentBanner from 'src/components/ShipmentBanner';
import StatusProgress from 'src/components/StatusProgress';
import parcelLabelImage from 'src/images/parcel-label.svg';
import addFileImage from 'src/images/parcel-label.svg';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Formik,
  FormInput,
  FormSubmit,
  Icon,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  toast,
  useApp
} from 'straightline-ui';
import {
  ACCESSORIAL_CODES,
  DOCUMENT_TYPE,
  ENTITY_TYPE,
  isCancelled,
  isConfirmed,
  isDelivered,
  isFTL,
  isLTL,
  isParcel,
  PROJECT_44,
  STATUS,
  statusType
} from 'straightline-utils/constants';
import { formatDistanceToNow } from 'straightline-utils/time';

export function Details({ shipment }) {
  return (
    <Row>
      <Col xl="7">
        {isParcel(shipment) && <ParcelLabelCard shipment={shipment} />}
        <StatusCard shipment={shipment} />
        <DetailsCard shipment={shipment} />
        <LineItemsCard shipment={shipment} />
        <EditCard shipment={shipment} />
      </Col>
      <Col xl="5">
        <CarrierCard shipment={shipment} />
        <LocationCard shipment={shipment} type="pickup" />
        <LocationCard shipment={shipment} type="delivery" />
      </Col>
    </Row>
  );
}

const ParcelLabelCard = ({ shipment }) => {
  if (!shipment.shipment_dispatch?.document || isDelivered(shipment)) {
    return null;
  }

  return (
    <Card>
      <CardBody className="px-3 p-md-4">
        <Row>
          <Col sm="4">
            {/* <button className="avatar avatar-xxl btn btn-outline-primary">
              <img
                src={parcelLabelImage}
                alt="Shipping Label"
                className="avatar-img rounded pointer"
                style={{ objectFit: 'scale-down' }}
              />
            </button> */}
            <img
              src={parcelLabelImage}
              alt="Shipping Label"
              className="img-fluid animated fadeIn"
            />
          </Col>
          <Col sm="8">
            <h3 className="font-weight-bold">
              Your label is ready to download!
            </h3>
            <div className="mb-3">
              Download and print your shipping label on any printer. Then
              clearly and securely attach the label to your package.
            </div>
            <div className="mb-3 text-danger">
              Straightline does not (yet) schedule parcel pickups. You should
              drop of your package at the nearest carrier location or add it to
              your regular pickup.
            </div>
            <div>
              <a
                className="btn-block btn btn-outline-primary"
                href={shipment.shipment_dispatch.document.download_url}
                download={`Straightline-Shipment-${shipment.id}-Label`}
              >
                <Icon type="download" className="mr-2" />
                Download Label
              </a>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const CarrierCard = ({ shipment }) => {
  const carrier = shipment.carrier;
  const dispatch = shipment.shipment_dispatch;
  const validMC =
    carrier &&
    carrier.mc_number &&
    carrier.mc_number.length &&
    carrier.mc_number !== '00000000';

  return (
    <Card>
      <CardBody className="p-3 p-md-4">
        <div className="mb-4">
          <ShipmentBanner shipment={shipment} />
        </div>
        <div className="border rounded p-3">
          <div className="d-flex align-items-center">
            <div className="mr-3">
              <CarrierLogo carrier={carrier} size={50} />
            </div>
            <div className="flex-grow-1">
              <div className="font-weight-bold">
                {carrier?.name ||
                  'This shipment has not been assigned a carrier'}
              </div>
              <div className="text-secondary">{shipment.mode}</div>
            </div>
          </div>
          <hr />
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div>Service</div>
            <div>{shipment.rate.service_level.name}</div>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div>Transit</div>
            <div>{shipment.rate.service_level.description}</div>
          </div>
          {validMC && (
            <>
              <hr />
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div>MC Number</div>
                <div>{carrier.mc_number}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <div>DOT Number</div>
                <div>{carrier.dot_number}</div>
              </div>
              {carrier.scac_code && (
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div>SCAC Code</div>
                  <div>{carrier.scac_code}</div>
                </div>
              )}
            </>
          )}
          {dispatch && (
            <>
              <hr />
              {dispatch.carrier_pro_number && (
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div>PRO Number</div>
                  <div>{dispatch.carrier_pro_number}</div>
                </div>
              )}
              {dispatch.carrier_trailer_number && (
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div>{isLTL(shipment) ? 'Pickup' : 'Trailer'} Number</div>
                  <div>{dispatch.carrier_trailer_number}</div>
                </div>
              )}
              {dispatch.carrier_truck_number && (
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div>Truck Number</div>
                  <div>{dispatch.carrier_truck_number}</div>
                </div>
              )}
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

const locationAccessorials = [
  ...ACCESSORIAL_CODES.PICKUP,
  ...ACCESSORIAL_CODES.DELIVERY
];

const DetailsCard = ({ shipment }) => {
  const app = useApp();

  const additionalAccessorials = shipment.accessorial_codes
    ? shipment.accessorial_codes.filter((code) => {
        return !locationAccessorials.find(({ value }) => value === code);
      })
    : null;

  const handleDownload = async () => {
    app
      .service('api/shipment-documents')
      .findOne({
        query: {
          shipment_id: shipment.id,
          '$document.document_type$': DOCUMENT_TYPE.SHIPMENT_INSURANCE,
          $sort: { created_at: -1 }
        }
      })
      .then((doc) => {
        if (!doc) {
          return toast.error('No insurance certificate found');
        }
        const anchor = document.createElement('a');
        anchor.setAttribute(
          'download',
          `Straightline-${shipment.id}-Insurance-Certificate`
        );
        anchor.href = doc.document.download_url;
        anchor.click();
      })
      .catch(app.toast.errors);
  };

  return (
    <Card>
      <CardBody className="p-3 p-md-4">
        {shipment.rate.insurance && (
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="text-uppercase text-muted font-weight-bold mb-0">
                Insurance
              </h5>
              {shipment.rate.insurance.provider_certificate_id && (
                <div>
                  <Button
                    color="link"
                    className="p-0 "
                    onClick={handleDownload}
                  >
                    Download Certificate
                  </Button>
                </div>
              )}
            </div>
            <div className="font-weight-bold">
              {shipment.rate.insurance.name}
            </div>
            <div className="">
              {shipment.rate.insurance.description} If you have any questions or
              need to file a claim, please{' '}
              <ContactModal
                config={{
                  subject: `App Contact - Shipment #${shipment.id}`,
                  service: 'api/shipments',
                  service_id: shipment.id
                }}
              >
                {(toggle) => (
                  <span className="text-primary pointer" onClick={toggle}>
                    Contact Straightline
                  </span>
                )}
              </ContactModal>
              .
            </div>
          </div>
        )}
        <div>
          <h5 className="text-uppercase text-muted font-weight-bold">Notes</h5>
          <div className="pre-line">
            {shipment.notes || (
              <span className="text-muted">Not available</span>
            )}
          </div>
        </div>
        {additionalAccessorials?.length > 0 && (
          <div className="mt-4">
            <h5 className="text-uppercase text-muted font-weight-bold">
              Additional Accessorials
            </h5>
            <div>
              {additionalAccessorials.map((code) => {
                const acc = PROJECT_44.ACCESSORIAL_CODES.find(
                  ({ value }) => value === code
                );
                return (
                  <Badge key={code} color="soft-primary" pill className="mr-2">
                    {acc?.label || code + ' (UNKNOWN ACCESSORIAL)'}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

const EditCard = ({ shipment }) => {
  const app = useApp();
  const shipmentsService = app.service('api/shipments');
  const schema = shipmentsService.schema;
  const handleSubmit = (data, formik) => {
    shipmentsService
      .patch(shipment.id, data)
      .then((shipment) => {
        toast.success(`Shipment updated!`);
        formik.setSubmitting(false);
        shipmentsService.invalidateGet(shipment.id);
      })
      .catch((err) => {
        toast.errors(err);
        if (err.errors) {
          formik.setErrors(err.errors);
        }
        formik.setSubmitting(false);
      });
  };

  return (
    <Formik
      initialValues={{
        reference_number: shipment.reference_number
      }}
      onSubmit={handleSubmit}
      validationSchema={schema}
      enableReinitialize={true}
      render={({ isSubmitting }) => {
        return (
          <Form>
            <Card>
              <CardBody className="p-3 p-md-4">
                <p>
                  Weight, Line Items, and notes cannot be edited after booking.
                  If you need to update these items, please{' '}
                  <ContactModal
                    config={{
                      subject: `App Contact - Shipment #${shipment.id}`,
                      service: 'api/shipments',
                      service_id: shipment.id
                    }}
                  >
                    {(toggle) => (
                      <span className="text-primary pointer" onClick={toggle}>
                        Contact Straightline
                      </span>
                    )}
                  </ContactModal>
                  .
                </p>
                <FormGroup>
                  <Label>Reference Number or Pickup Number</Label>
                  <FormInput
                    name="reference_number"
                    placeholder="PO number, RFQ number, or any other internal reference"
                  />
                  <div className="text-muted mt-1 mb-2">
                    Carriers may use this number as the pickup number for your
                    shipment.
                  </div>
                  <FormFeedback name="reference_number" />
                </FormGroup>
                <FormSubmit>Submit</FormSubmit>
              </CardBody>
            </Card>
          </Form>
        );
      }}
    />
  );
};

const StatusCard = ({ shipment, onSuccess, onError }) => {
  if (isCancelled(shipment)) {
    return (
      <Card>
        <CardBody className="px-3 px-md-4">
          <Row className="justify-content-between align-items-center mb-3">
            <Col xs="auto">
              <div className="h3 mb-0 d-flex align-items-center">
                <Icon type="activity" className="mr-3" />
                Load Status
              </div>
            </Col>
            <Col xs="auto">
              <div className="progress progress-status">
                <div
                  className="bg-danger progress-bar"
                  style={{ width: '100%' }}
                >
                  Cancelled
                </div>
              </div>
            </Col>
          </Row>
          <div>
            This shipment has been cancelled. Don't worry! you won't be charged
            for this shipment. Repeat the shipment to get another quote. If you
            have any questions, please{' '}
            <ContactModal
              config={{
                subject: `App Contact - Shipment #${shipment.id}`,
                service: 'api/shipments',
                service_id: shipment.id
              }}
            >
              {(toggle) => (
                <span className="text-primary pointer" onClick={toggle}>
                  Contact Straightline
                </span>
              )}
            </ContactModal>
            .
          </div>
        </CardBody>
        <div className="border-top">
          <RepeatShipmentItem shipment={shipment} />
        </div>
      </Card>
    );
  }

  if (isDelivered(shipment)) {
    return (
      <Card>
        <CardBody className="px-3 px-md-4">
          <Row className="justify-content-between align-items-center mb-3">
            <Col xs="auto">
              <div className="h3 mb-0 d-flex align-items-center">
                <Icon type="activity" className="mr-3" />
                Load Status
              </div>
            </Col>
            <Col xs="auto">
              <StatusProgress shipment={shipment} unshift trim />
            </Col>
          </Row>
          <div>
            This shipment has been successfully delivered! You can download the
            documents, review your invoice, or repeat this shipment. To make
            future shipments even easier, your preferences have been saved for
            quick access and faster booking. If you have any questions, please{' '}
            <ContactModal
              config={{
                subject: `App Contact - Shipment #${shipment.id}`,
                service: 'api/shipments',
                service_id: shipment.id
              }}
            >
              {(toggle) => (
                <span className="text-primary pointer" onClick={toggle}>
                  Contact Straightline
                </span>
              )}
            </ContactModal>
            .
          </div>
        </CardBody>
        <div className="border-top">
          <InvoiceItem shipment={shipment} />
          <PodItem shipment={shipment} />
          <RepeatShipmentItem shipment={shipment} />
          <TrackingHistory shipment={shipment} />
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <StatusItem shipment={shipment} />
        <HazmatDocumentItem shipment={shipment} />
        <InternationalDocumentItem shipment={shipment} />
        <BolItem shipment={shipment} />
        <AppointmentItem shipment={shipment} />
        <ShareItem shipment={shipment} />
        <DriverItem shipment={shipment} />
        <EtaItem shipment={shipment} type="pickup" />
        <EtaItem shipment={shipment} type="delivery" />
        <TrackingHistory shipment={shipment} />
      </Card>
      <Card>
        <CardBody className="p-3 p-md-4">
          <ShipmentNotifications shipment={shipment} />
        </CardBody>
      </Card>
    </>
  );
};

function AppointmentItem({ shipment }) {
  if (!isFTL(shipment)) {
    return null;
  }

  const hasPickup = shipment.accessorial_codes?.includes('APPTPU');
  const hasDelivery = shipment.accessorial_codes?.includes('APPTDEL');

  if (!hasPickup && !hasDelivery) {
    return null;
  }

  const needsPickup =
    hasPickup && !shipment.pickup_address?.appointment_confirmed;
  const needsDelivery =
    hasDelivery && !shipment.delivery_address?.appointment_confirmed;
  const isReady = isConfirmed(shipment);

  return (
    <CardBody className="border-bottom px-3 px-md-4">
      <Row className="align-items-center justify-content-between mb-2">
        <Col xs="auto" className="">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="watch" className="mr-3" />
            <span className="d-none d-sm-inline">Appointments</span>
            <span className="d-sm-none">Appts</span>
            {hasPickup && (
              <Fragment>
                {needsPickup ? (
                  <Badge color="soft-secondary" className="ml-3">
                    Pickup
                  </Badge>
                ) : (
                  <Badge color="soft-primary" className="ml-3">
                    <Icon type="check" className="mr-1" />
                    Pickup
                  </Badge>
                )}
              </Fragment>
            )}
            {hasDelivery && (
              <Fragment>
                {needsDelivery ? (
                  <Badge color="soft-secondary" className="ml-3">
                    Delivery
                  </Badge>
                ) : (
                  <Badge color="soft-primary" className="ml-3">
                    <Icon type="check" className="mr-1" />
                    Delivery
                  </Badge>
                )}
              </Fragment>
            )}
          </div>
        </Col>
        <Col xs="auto" className="text-right">
          <ContactModal
            header="Confirm Appointment"
            text="Let us know the specific details of your appointment and we will confirm it with the pickup and delivery contacts."
            config={{
              subject: `Confirm Appointment - Shipment #${shipment.id}`,
              service: 'api/shipments',
              service_id: shipment.id,
              header: `${shipment.pickup_address.city}, ${shipment.pickup_address.state} ⇢ ${shipment.delivery_address.city}, ${shipment.delivery_address.state}`,
              subHeader: `${shipment.pickup_address.appointment} ⇢ ${shipment.delivery_address.appointment}`
            }}
          >
            {(toggle) => (
              <Button color="link" onClick={toggle}>
                Confirm
              </Button>
            )}
          </ContactModal>
        </Col>
      </Row>
      <div>
        {isReady
          ? 'All appointments have been confirmed! If you need to change an appointment, click "Confirm" and Straightline will attempt to update your appointment with the carrier.'
          : 'Straightline is coordinating with the pickup and delivery contacts to confirm appointments. Click "Confirm" to contact Straightline with the details of your appointment.'}
      </div>
    </CardBody>
  );
}

function PodItem({ shipment }) {
  const app = useApp();

  const { result: podDocument } = app
    .service('api/shipment-documents')
    .useFindOne(
      {
        query: {
          shipment_id: shipment.id,
          '$document.document_type$': DOCUMENT_TYPE.PROOF_OF_DELIVERY,
          $sort: { created_at: -1 }
        }
      },
      { keepPreviousData: true }
    );

  return (
    <CardBody className="border-bottom px-3 px-md-4">
      <Row className="align-items-center justify-content-between mb-2">
        <Col xs="auto" className="">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="file" className="mr-3" />
            <span className="d-none d-sm-inline">Proof of Delivery</span>
            <span className="d-sm-none">POD</span>
            {podDocument ? (
              <Badge color="soft-primary" className="ml-3">
                <Icon type="check" className="mr-1" />
                Available
              </Badge>
            ) : (
              <Badge color="soft-secondary" className="ml-3">
                Waiting
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto" className="text-right">
          <Button
            color="link"
            tag="a"
            disabled={!podDocument?.document.download_url}
            href={podDocument?.document.download_url}
            download={`Straightline-${shipment.id}-POD.${podDocument?.document.format}`}
          >
            Download
          </Button>
        </Col>
      </Row>
      <div>
        {podDocument
          ? 'Download the POD for this shipment. The POD is a legal document that confirms the delivery of the shipment. It is important to keep this document for your records.'
          : 'The carrier has not yet uploaded the POD. When the POD is uploaded you will be notified and the document will be available for download.'}
      </div>
    </CardBody>
  );
}

function RepeatShipmentItem({ shipment }) {
  return (
    <CardBody className="border-bottom px-3 px-md-4">
      <Row className="align-items-center justify-content-between mb-2">
        <Col xs="auto" className="">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="repeat" className="mr-3" />
            <span className="d-none d-sm-inline">Repeat Shipment</span>
            <span className="d-sm-none">Repeat</span>
          </div>
        </Col>
        <Col xs="auto" className="text-right">
          <Button
            to="/quote"
            query={{ copied_shipment_id: shipment.id }}
            color="link"
          >
            New Quote
          </Button>
        </Col>
      </Row>
      <div>
        Get a new quote for this shipment by clicking "New Quote". This will
        copy the shipment details and create a new quote. You can then edit the
        quote and book the shipment again.
      </div>
    </CardBody>
  );
}

function InvoiceItem({ shipment }) {
  const app = useApp();

  const { result: invoice, isLoading } = app
    .service('api/shipment-invoices')
    .useFindOne(
      {
        query: {
          shipment_id: shipment.id,
          $sort: { created_at: -1 },
          $select: ['id', 'shipment_id']
        }
      },
      { keepPreviousData: true }
    );

  if (isLoading) {
    return null;
  }

  return (
    <CardBody className="border-bottom px-3 px-md-4">
      <Row className="align-items-center justify-content-between mb-2">
        <Col xs="auto" className="">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="dollar-sign" className="mr-3" />
            Invoice
            {invoice ? (
              <Badge color="soft-primary" className="ml-3">
                <Icon type="check" className="mr-1" />
                Available
              </Badge>
            ) : (
              <Badge color="soft-secondary" className="ml-3">
                Waiting
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto" className="text-right">
          <Button
            color="link"
            tag="a"
            disabled={!invoice?.secure_url}
            href={invoice?.secure_url + '?download=true'}
          >
            Download
          </Button>
        </Col>
      </Row>
      <div>
        {invoice
          ? 'Your invoice is ready to download! The invoice includes all charges and fees for this shipment. Please review the invoice details and make a payment at your earliest convenience.'
          : 'This shipment has not been invoiced yet. The invoice will be available after the shipment has been delivered and the POD has been uploaded.'}
      </div>
    </CardBody>
  );
}

function EtaItem({ shipment, type }) {
  const app = useApp();
  const etaType = type;
  const etaAddress = shipment[`${etaType}_address`];
  const etaLabel = etaType === 'pickup' ? 'Pickup' : 'Delivery';

  const {
    result: eta,
    isLoading,
    isError
  } = app.service('api/shipment-etas').useFindOne(
    {
      query: {
        shipment_id: shipment.id,
        type: etaType,
        $sort: { created_at: -1 }
      }
    },
    { keepPreviousData: true }
  );

  if (isLoading || isError) {
    return null;
  }

  return (
    <CardBody className="border-bottom px-3 px-md-4">
      <Row className="align-items-center justify-content-between mb-3">
        <Col xs="auto" className="">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="clock" className="mr-3" />
            <span className="d-none d-sm-inline">Estimated {etaLabel}</span>
            <span className="d-sm-none">{etaLabel} ETA</span>
            {eta ? (
              <Badge color="soft-primary" className="ml-3">
                <Icon type="check" className="mr-1" />
                ETA Set
              </Badge>
            ) : (
              <Badge color="soft-secondary" className="ml-3">
                Waiting
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto" className="text-right"></Col>
      </Row>
      {eta ? (
        <div>
          <div className="py-3 px-4 border-left border-primary bg-lighter mb-3">
            <div className="mb-2 d-flex align-items-start">
              <Icon type="calendar" className="text-secondary mr-3" />
              <div>
                <div className="text-primary font-weight-bold">
                  {new Date(eta.date).toLocaleDateString('en-US', {
                    timeZone: etaAddress.timezone,
                    timeZoneName: 'short',
                    hour: 'numeric',
                    minute: 'numeric',
                    month: 'short',
                    weekday: 'short',
                    day: 'numeric'
                  })}
                </div>
                <div className="text-secondary">
                  last updated {formatDistanceToNow(eta.created_at)}
                </div>
              </div>
            </div>
            <div className="d-flex align-items-start">
              <Icon type="file-text" className="text-secondary mr-3" />
              {eta.notes || (
                <span className="text-secondary">
                  No notes provided by carrier.
                </span>
              )}
            </div>
          </div>
          <div>
            The carrier may update the ETA as the shipment progresses.
            Notifications will be sent to you and the {eta.type} contact as
            ETA's are updated.
          </div>
        </div>
      ) : (
        <div>
          The shipment's estimated arrival time has not been determined yet.
          Straightline is coordinating with the carrier to establish an ETA.
        </div>
      )}
    </CardBody>
  );
}

function BolItem({ shipment }) {
  const app = useApp();

  const { result: bolDocument } = app
    .service('api/shipment-documents')
    .useGet('bol', {
      query: {
        shipment_id: shipment.id
      }
    });

  let isReady = !!shipment.carrier_id && !!shipment.shipment_dispatch;
  if (isReady && isLTL(shipment)) {
    isReady =
      !!shipment.shipment_dispatch.carrier_pro_number ||
      !!shipment.shipment_dispatch.carrier_trailer_number;
  }

  return (
    <CardBody className="border-bottom px-3 px-md-4">
      <Row className="justify-content-between align-items-center mb-2">
        <Col xs="auto">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="download" className="mr-3" />
            <span className="d-none d-sm-inline">Bill of Lading</span>
            <span className="d-sm-none">BOL</span>
            {isReady ? (
              <Badge color="soft-primary" className="ml-3">
                <Icon type="check" className="mr-1" />
                Available
              </Badge>
            ) : (
              <Badge color="soft-secondary" className="ml-3">
                Waiting
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto">
          <Button
            color="link"
            tag="a"
            disabled={!isReady || !bolDocument?.document.download_url}
            href={bolDocument?.document.download_url}
          >
            Download
          </Button>
        </Col>
      </Row>
      {isReady ? (
        <div>
          Download the BOL for this shipment. Use the BOL to confirm shipment
          and carrier details when picking up. Once confirmed, provide the BOL
          to the driver for delivery.
        </div>
      ) : (
        <div>
          This shipment is still awaiting some dispatch details from the carrier
          or driver. The BOL will be available after the dispatch has been
          confirmed.
        </div>
      )}
    </CardBody>
  );
}

function DriverItem({ shipment }) {
  if (!isFTL(shipment)) {
    return null;
  }

  const shipment_dispatch = shipment.shipment_dispatch;

  return (
    <CardBody className="border-bottom px-3 px-md-4">
      <Row className="align-items-center justify-content-between mb-3">
        <Col xs="auto">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="user" className="mr-3" />
            Driver
            {!!shipment_dispatch ? (
              <Badge color="soft-primary" className="ml-3">
                <Icon type="check" className="mr-1" />
                Dispatched
              </Badge>
            ) : (
              <Badge color="soft-secondary" className="ml-3">
                Waiting
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto"></Col>
      </Row>
      {shipment_dispatch && (
        <div className="py-3 px-4 border-left border-primary bg-lighter mb-3">
          <div className="mb-1">
            <Icon type="user" className="text-secondary mr-3" />
            {shipment_dispatch.carrier_driver}
          </div>
          <div className="mb-1">
            <Icon type="phone" className="text-secondary mr-3" />
            {shipment_dispatch.carrier_phone ? (
              <a href={`tel:${shipment_dispatch.carrier_phone}`}>
                {shipment_dispatch.carrier_phone}
              </a>
            ) : (
              <span className="text-secondary">Not Available</span>
            )}
          </div>
          <div className="mb-1">
            <Icon type="truck" className="text-secondary mr-3" />
            {shipment_dispatch?.carrier_truck_number || (
              <span className="text-secondary">Not Available</span>
            )}
          </div>
          <div className="">
            <Icon type="book" className="text-secondary mr-3" />
            {shipment_dispatch?.carrier_trailer_number || (
              <span className="text-secondary">Not Available</span>
            )}
          </div>
        </div>
      )}
      {shipment_dispatch ? (
        <div>
          Verify the driver's contact information, truck number, and trailer
          number upon arrival. This information is also available on the BOL.
        </div>
      ) : (
        <div>
          This shipment has not yet been dispatched to a driver. Once
          dispatched, the driver's contact information, truck number, and
          trailer number will be available here.
        </div>
      )}
    </CardBody>
  );
}

function HazmatDocumentItem({ shipment }) {
  const app = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const hasHazmat = shipment.accessorial_codes?.includes('HAZM');

  const { result } = app.service('api/shipment-documents').useFindOne(
    {
      query: {
        $sort: { created_at: -1 },
        shipment_id: shipment.id,
        '$document.document_type$': DOCUMENT_TYPE.HAZM
      }
    },
    { keepPreviousData: true, enabled: hasHazmat }
  );

  const handleClick = () => {
    inputRef.current.value = null;
    inputRef.current.click();
  };

  const handleImageChange = (event) => {
    setIsLoading(true);
    const files = Array.from(event.target.files);
    return Promise.all(
      files.map((file) => {
        const data = {
          file,
          name: `Shipment #${shipment.id} Hazmat`,
          document_type: DOCUMENT_TYPE.HAZM
        };

        return app
          .service('api/documents')
          .create(data)
          .then((document) => {
            return app.service('api/shipment-documents').create({
              shipment_id: shipment.id,
              document_id: document.id,
              entity_types: [ENTITY_TYPE.SHIPPER]
            });
          })
          .catch((err) => toast.errors(err));
      })
    ).then(async () => {
      toast.success('Hazmat document uploaded successfully');
      app.service('api/shipment-documents').invalidateFind();
      app.service('api/shipments').invalidateGet(shipment.id);
      app.service('api/shipments').invalidateFind();
      setIsLoading(false);
    });
  };

  if (!hasHazmat) {
    return null;
  }

  if (!result) {
    return (
      <CardBody className="border-bottom px-3 p-md-4">
        <input
          type="file"
          accept=".jpg, .png, .pdf, .jpeg"
          multiple
          className="d-none"
          ref={inputRef}
          onChange={handleImageChange}
        />
        <Row className="align-items-center justify-content-between mb-2">
          <Col xs="auto">
            <div className="h3 mb-0 d-flex align-items-center">
              <Icon type="alert-octagon" className="mr-3" />
              <span className="">Hazmat</span>
            </div>
          </Col>
          <Col xs="auto">
            <Button
              color="link"
              onClick={handleClick}
              loading={isLoading}
              disabled={isLoading}
            >
              Upload
            </Button>
          </Col>
        </Row>
        <div>
          Choose a file to upload as Hazmat documentation. Please ensure the
          file contains detailed information about the material, its
          classification, and handling instructions for first responders in case
          of an incident.
        </div>
      </CardBody>
    );
  }

  const hazmatDocument = result;

  return (
    <CardBody className="border-bottom px-3 p-md-4">
      <input
        type="file"
        accept=".jpg, .png, .pdf, .jpeg"
        multiple
        className="d-none"
        ref={inputRef}
        onChange={handleImageChange}
      />
      <Row className="align-items-center justify-content-between mb-2">
        <Col xs="auto">
          <div
            className={`h3 mb-0 d-flex align-items-center ${
              !hazmatDocument ? 'text-danger' : ''
            }`}
          >
            <Icon type="alert-octagon" className="mr-3" />
            <span>Hazmat</span>
            {hazmatDocument ? (
              <Badge color="soft-primary" className="ml-3">
                <Icon type="check" className="mr-1" />
                Uploaded
              </Badge>
            ) : (
              <Badge color="soft-danger" className="ml-3">
                Attention
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto">
          <Button
            color="link"
            onClick={handleClick}
            loading={isLoading}
            disabled={isLoading}
          >
            Upload
          </Button>
        </Col>
      </Row>
      <div>
        Choose a file to upload as Hazmat documentation. Please ensure the file
        contains detailed information about the material, its classification,
        and handling instructions for first responders in case of an incident.
      </div>
    </CardBody>
  );
}

function InternationalDocumentItem({ shipment }) {
  const app = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const hasIntl =
    shipment.pickup_address.country !== shipment.delivery_address.country;

  const { result } = app.service('api/shipment-documents').useFindOne(
    {
      query: {
        $sort: { created_at: -1 },
        shipment_id: shipment.id,
        '$document.document_type$': DOCUMENT_TYPE.INTL
      }
    },
    { keepPreviousData: true, enabled: hasIntl }
  );

  if (!hasIntl) {
    return null;
  }

  const intlDocument = result;

  const handleClick = () => {
    inputRef.current.value = null;
    inputRef.current.click();
  };

  const handleImageChange = (event) => {
    setIsLoading(true);
    const files = Array.from(event.target.files);
    return Promise.all(
      files.map((file) => {
        const data = {
          file,
          name: `Shipment #${shipment.id} International`,
          document_type: DOCUMENT_TYPE.INTL
        };

        return app
          .service('api/documents')
          .create(data)
          .then((document) => {
            return app.service('api/shipment-documents').create({
              shipment_id: shipment.id,
              document_id: document.id,
              entity_types: [ENTITY_TYPE.SHIPPER]
            });
          })
          .catch((err) => toast.errors(err));
      })
    ).then(async () => {
      toast.success('Customs document uploaded successfully');
      app.service('api/shipment-documents').invalidateFind();
      app.service('api/shipments').invalidateGet(shipment.id);
      app.service('api/shipments').invalidateFind();
      setIsLoading(false);
    });
  };

  if (!result) {
    return (
      <CardBody className="border-bottom px-3 p-md-4">
        <input
          type="file"
          accept=".jpg, .png, .pdf, .jpeg"
          multiple
          className="d-none"
          ref={inputRef}
          onChange={handleImageChange}
        />
        <Row className="align-items-center justify-content-between mb-2">
          <Col xs="auto">
            <div className="h3 mb-0 d-flex align-items-center">
              <Icon type="paperclip" className="mr-3" />
              <span className="">International</span>
            </div>
          </Col>
          <Col xs="auto">
            <Button
              color="link"
              onClick={handleClick}
              loading={isLoading}
              disabled={isLoading}
            >
              Upload
            </Button>
          </Col>
        </Row>
        <div>
          Choose a file to upload as Customs documentation. The file may include
          commercial invoice packing list, certificate of origin, and
          potentially insurance certificates, export/import licenses, and
          customs declarations.
        </div>
      </CardBody>
    );
  }

  return (
    <CardBody className="border-bottom px-3 p-md-4">
      <input
        type="file"
        accept=".jpg, .png, .pdf, .jpeg"
        multiple
        className="d-none"
        ref={inputRef}
        onChange={handleImageChange}
      />
      <Row className="align-items-center justify-content-between mb-2">
        <Col xs="auto">
          <div
            className={`h3 mb-0 d-flex align-items-center ${
              !intlDocument ? 'text-danger' : ''
            }`}
          >
            <Icon type="paperclip" className="mr-3" />
            <span>International</span>
            {intlDocument ? (
              <Badge color="soft-primary" className="ml-3">
                <Icon type="check" className="mr-1" />
                Uploaded
              </Badge>
            ) : (
              <Badge color="soft-danger" className="ml-3">
                Attention
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto">
          <Button
            color="link"
            onClick={handleClick}
            loading={isLoading}
            disabled={isLoading}
          >
            Upload
          </Button>
        </Col>
      </Row>
      <div>
        Choose a file to upload as Customs documentation. The file may include
        commercial invoice packing list, certificate of origin, and potentially
        insurance certificates, export/import licenses, and customs
        declarations.
      </div>
    </CardBody>
  );
}

function ShareItem({ shipment }) {
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const toggleShareModal = () => setShareModalOpen(!shareModalOpen);

  return (
    <CardBody className="border-bottom px-3 p-md-4">
      <Row className="align-items-center justify-content-between mb-2">
        <Col xs="auto">
          <div className={`h3 mb-0 d-flex align-items-center `}>
            <Icon type="share-2" className="mr-3" />
            <span>Share</span>
          </div>
        </Col>
        <Col xs="auto">
          <Button color="link" onClick={toggleShareModal}>
            Share
          </Button>
        </Col>
      </Row>
      <div>
        Share a public link to this shipment that allows users to see the BOL,
        details, and tracking information. Pickup and Delivery contacts
        automatically receive a link to the shipment via email.
      </div>
      <ShareModal
        isOpen={shareModalOpen}
        toggle={toggleShareModal}
        shipment={shipment}
      />
    </CardBody>
  );
}

const statusTexts = {
  [STATUS.BOOKED]: `The shipment has been quoted and officially booked with Straightline. We are now working on finding a carrier for you. Once a carrier is assigned, the shipment will then be moved to the "Matched" status.`,
  [STATUS.MATCHED]: `A carrier has been assigned to this shipment and is beginning preparations for pickup. The carrier will soon assign a driver and mark this shipment as "Dispatched". You can then download the BOL and prepare for pickup.`,
  [STATUS.DISPATCHED]: `A driver has been assigned to this shipment and is on their way to the pickup location. You can expect an ETA from the driver soon. Please prepare for pickup by ensuring the cargo is ready to load, the BOL is printed, and that the driver has access to the pickup location.`,
  [STATUS.PICKUP]: `The driver has arrived at the pickup location and is preparing to load the shipment. Please provide the BOL to the driver, ensure they have access to the pickup location, and assist them with any questions or concerns they may have.`,
  [STATUS.TRANSIT]: `The shipment is currently in transit and on its way to the delivery location. You will receive an ETA for delivery soon. Please ensure the delivery location is prepared for unload and that the driver has access to the location.`,
  [STATUS.DELIVERY]: `The driver has arrived at the delivery location and is preparing to unload the shipment. Please ensure the driver has access to the delivery location and assist them with any questions or concerns they may have.`,
  [STATUS.DELIVERED]: `The shipment has been successfully dropped off. Soon the driver will upload the POD and finalize the shipment. You will then be able to download the POD and view the billing details.`,
  [STATUS.CANCELLED]: `The shipment has been cancelled. Please contact Straightline if you have any questions or concerns.`
};

function StatusItem({ shipment }) {
  const app = useApp();

  const handleClick = (status) => {
    toast.confirm({
      title: 'Update Status',
      message: (
        <div>
          <p>Are you sure you want to set the status to "{status.label}"?</p>
          <div className="p-3 bg-light rounded">
            {statusTexts[status.value]}
          </div>
        </div>
      ),
      okButtonText: 'Yes, update status',
      cancelButtonText: 'Nevermind, do not update',
      handleOnOk: () => {
        return app
          .service('api/shipments')
          .patch(shipment.id, { status_type: status.value })
          .then((updateShipment) => {
            toast.success(`Shipment #${shipment.id} is "${status.label}"`);
            app.service('api/shipments').invalidateGet(shipment.id);
            app.service('api/shipments').invalidateFind();
          })
          .catch((error) => {
            toast.errors(error);
          });
      }
    });
  };

  const changeOptions = shipment.status_change_options
    .filter((option) => !isCancelled(option))
    .map((option) => statusType(option));

  const currentStatus = statusType(shipment.status_type);

  return (
    <CardBody className="border-bottom px-3 px-md-4">
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs="auto">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="activity" className="mr-3" />
            Load Status
          </div>
        </Col>
        <Col xs="auto">
          <StatusProgress
            shipment={shipment}
            onClick={handleClick}
            unshift
            trim
          />
        </Col>
      </Row>
      <div>
        {statusTexts[currentStatus.value]}
        {changeOptions.length > 0 && (
          <React.Fragment>
            {' '}
            Click{' '}
            {changeOptions.length === 1 ? (
              <span
                onClick={() => handleClick(changeOptions[0])}
                className="text-primary pointer"
              >
                {changeOptions[0].label}
              </span>
            ) : (
              changeOptions.map((option, index) => (
                <span key={index}>
                  {index === 0 ? '' : ', or '}
                  <span
                    onClick={() => handleClick(option)}
                    className="text-primary pointer"
                  >
                    {option.label}
                  </span>
                </span>
              ))
            )}{' '}
            to update to the latest status. If you need to change to another
            status, please{' '}
            <ContactModal
              config={{
                subject: `App Contact - Shipment #${shipment.id}`,
                service: 'api/shipments',
                service_id: shipment.id
              }}
            >
              {(toggle) => (
                <span className="text-primary pointer" onClick={toggle}>
                  Contact Straightline
                </span>
              )}
            </ContactModal>
            .
          </React.Fragment>
        )}
      </div>
    </CardBody>
  );
}

const formatAddress = ({ city, state, postal_code, country }) => {
  const strings = [state, postal_code, country].filter(Boolean);
  if (!city) {
    return strings.join(' ');
  }
  if (!strings.length) {
    return city;
  }
  return `${city}, ${strings.join(' ')}`;
};

const TrackingHistory = ({ shipment }) => {
  const app = useApp();
  const [showMore, setShowMore] = useState(false);

  let {
    result: tracking,
    isLoading,
    isError,
    error
  } = app.service('api/shipment-tracking').useFind(
    {
      query: {
        shipment_id: shipment.id,
        carrier_id: shipment.carrier_id,
        $sort: { carrier_timestamp: -1 },
        $limit: 100
      }
    },
    {
      keepPreviousData: true,
      refetchInterval: 1000 * 60 * 5
    }
  );

  if (isLoading) {
    return null;
  }

  if (isError) {
    return (
      <div className="px-4 py-3 border-bottom text-danger">
        {error.message || 'There was an error loading your tracking history.'}
      </div>
    );
  }

  const latestTrack = tracking.data[0];

  if (!latestTrack) {
    return (
      <CardBody className="px-3 px-md-4">
        <Row className="align-items-center justify-content-between mb-3">
          <Col xs="auto">
            <div className="h3 mb-0 d-flex align-items-center">
              <Icon type="map" className="mr-3" />
              Tracking
              <Badge color="soft-secondary" className="ml-3">
                Waiting
              </Badge>
            </div>
          </Col>
          <Col xs="auto"></Col>
        </Row>
        <div>
          The carrier has not yet provided tracking information for this
          shipment. Tracking information may be available when the carrier
          dispatches the load.
        </div>
      </CardBody>
    );
  }

  const latestEvents = tracking.data.slice(0, 3);
  const restEvents = tracking.data.slice(3);

  return (
    <CardBody className="px-3 px-md-4">
      <Row className="align-items-center justify-content-between mb-3">
        <Col xs="auto">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="compass" className="mr-3" />
            Tracking
            {!isDelivered(shipment) && (
              <Badge color="soft-primary" className="ml-3">
                <Icon type="check" className="mr-1" />
                Active
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto"></Col>
      </Row>
      <div className="py-3 px-4 border-left border-primary bg-lighter mb-3">
        {latestEvents.map((track, index) => {
          return (
            <div
              key={track.id}
              className={`d-flex ${index !== 0 ? 'mt-3' : ''}`}
            >
              <div>
                <Icon type="map-pin" className="text-secondary mr-3" />
              </div>
              <div className="flex-grow-1">
                <div className="font-weight-bold text-primary">
                  {formatAddress(track) || 'Unknown Location'}
                </div>
                <div className="text-secondary">
                  {new Date(track.carrier_timestamp).toLocaleDateString(
                    'en-US',
                    {
                      timeZoneName: 'short',
                      hour: 'numeric',
                      minute: 'numeric',
                      month: 'short',
                      weekday: 'short',
                      day: 'numeric'
                    }
                  )}
                </div>
                <div>
                  {track.description || (
                    <span className="text-secondary">Notes Not Available</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {restEvents.length > 0 && (
          <Button
            size="sm"
            color="white"
            className="mt-3"
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? 'Show Less' : `Show ${restEvents.length} More`}
          </Button>
        )}
        {showMore &&
          restEvents.map((track, index) => {
            return (
              <div key={track.id} className="d-flex mt-3">
                <div>
                  <Icon type="map-pin" className="text-secondary mr-3" />
                </div>
                <div className="flex-grow-1">
                  <div className="font-weight-bold text-primary">
                    {formatAddress(track) || 'Unknown Location'}
                  </div>
                  <div className="text-secondary">
                    {new Date(track.carrier_timestamp).toLocaleDateString(
                      'en-US',
                      {
                        timeZoneName: 'short',
                        hour: 'numeric',
                        minute: 'numeric',
                        month: 'short',
                        weekday: 'short',
                        day: 'numeric'
                      }
                    )}
                  </div>
                  <div>
                    {track.description || (
                      <span className="text-secondary">
                        Notes Not Available
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      {!isDelivered(shipment) && (
        <div className="">
          Carriers may provide additional tracking information as the shipment
          is in transit. View the map at the top of the page for the most recent
          location and route information
        </div>
      )}
    </CardBody>
  );
};

export function HazmatModal({ open, toggle, shipment, onComplete }) {
  return (
    <Modal
      isOpen={open}
      toggle={toggle}
      modalClassName="modal-fullscreen-sm"
      scrollable
      centered
      fade={false}
    >
      {() => {
        return (
          <HazmatModalContent
            shipment={shipment}
            toggle={toggle}
            onComplete={onComplete}
          />
        );
      }}
    </Modal>
  );
}

function HazmatModalContent({ toggle, shipment, onComplete }) {
  const app = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);

  const handleClick = () => {
    inputRef.current.value = null;
    inputRef.current.click();
  };

  const handleImageChange = (event) => {
    setIsLoading(true);
    const files = Array.from(event.target.files);
    return Promise.all(
      files.map((file) => {
        const data = {
          file,
          name: `Shipment #${shipment.id} Hazmat`,
          document_type: DOCUMENT_TYPE.HAZM
        };

        return app
          .service('api/documents')
          .create(data)
          .then((document) => {
            return app.service('api/shipment-documents').create({
              shipment_id: shipment.id,
              document_id: document.id
            });
          })
          .catch((err) => toast.errors(err));
      })
    ).then(async () => {
      toast.success('Hazmat document uploaded successfully');
      app.service('api/shipment-documents').invalidateFind();
      app.service('api/shipments').invalidateFind();
      app.service('api/shipments').invalidateGet(shipment.id);
      setIsLoading(false);
      if (onComplete) {
        onComplete();
      }
    });
  };

  return (
    <div className="modal-content h-100">
      <input
        type="file"
        accept=".jpg, .png, .pdf, .jpeg"
        multiple
        className="d-none"
        ref={inputRef}
        onChange={handleImageChange}
      />
      <ModalBody className="p-3 p-sm-4">
        <div className="d-flex justify-content-between mb-4">
          <div>
            <h2 className="mb-0">Upload Hazmat Document</h2>
            Include all required documents
          </div>
          <div>
            <Button type="button" close onClick={toggle}></Button>
          </div>
        </div>
        <div className="mb-4">
          <img
            src={addFileImage}
            alt="Upload Hazmat Document"
            className="img-fluid"
          />
        </div>
        <div>
          Choose a file to upload as Hazmat documentation. Please ensure the
          file contains detailed information about the material, its
          classification, and handling instructions for first responders in case
          of an incident.
        </div>
      </ModalBody>
      <ModalFooter className="p-3 p-sm-4">
        <Button
          color="primary"
          block
          onClick={handleClick}
          loading={isLoading}
          disabled={isLoading}
        >
          Upload Document
        </Button>
      </ModalFooter>
    </div>
  );
}

export function InternationalModal({ open, toggle, shipment, onComplete }) {
  return (
    <Modal
      isOpen={open}
      toggle={toggle}
      modalClassName="modal-fullscreen-sm"
      scrollable
      centered
      fade={false}
    >
      {() => {
        return (
          <InternationalModalContent
            shipment={shipment}
            toggle={toggle}
            onComplete={onComplete}
          />
        );
      }}
    </Modal>
  );
}

function InternationalModalContent({ toggle, shipment, onComplete }) {
  const app = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);

  const handleClick = () => {
    inputRef.current.value = null;
    inputRef.current.click();
  };

  const handleImageChange = (event) => {
    setIsLoading(true);
    const files = Array.from(event.target.files);
    return Promise.all(
      files.map((file) => {
        const data = {
          file,
          name: `Shipment #${shipment.id} International`,
          document_type: DOCUMENT_TYPE.INTL
        };

        return app
          .service('api/documents')
          .create(data)
          .then((document) => {
            return app.service('api/shipment-documents').create({
              shipment_id: shipment.id,
              document_id: document.id
            });
          })
          .catch((err) => toast.errors(err));
      })
    ).then(async () => {
      toast.success('Customs document uploaded successfully');
      app.service('api/shipment-documents').invalidateFind();
      app.service('api/shipments').invalidateFind();
      app.service('api/shipments').invalidateGet(shipment.id);
      setIsLoading(false);
      if (onComplete) {
        onComplete();
      }
    });
  };

  return (
    <div className="modal-content h-100">
      <input
        type="file"
        accept=".jpg, .png, .pdf, .jpeg"
        multiple
        className="d-none"
        ref={inputRef}
        onChange={handleImageChange}
      />
      <ModalBody className="p-3 p-sm-4">
        <div className="d-flex justify-content-between mb-4">
          <div>
            <h2 className="mb-0">Upload Customs Document</h2>
            Include all required documents
          </div>
          <div>
            <Button type="button" close onClick={toggle}></Button>
          </div>
        </div>
        <div className="mb-4">
          <img
            src={addFileImage}
            alt="Upload Customs Document"
            className="img-fluid"
          />
        </div>
        <div>
          Choose a file to upload as Customs documentation. The file may include
          commercial invoice, packing list, certificate of origin, and
          potentially insurance certificates, export/import licenses, customs
          declarations, and broker contact information.
        </div>
      </ModalBody>
      <ModalFooter className="p-3 p-sm-4">
        <Button
          color="primary"
          block
          onClick={handleClick}
          loading={isLoading}
          disabled={isLoading}
        >
          Upload Document
        </Button>
      </ModalFooter>
    </div>
  );
}
